import React, { useState, useEffect, useRef } from 'react';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Typography,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import DocumentMSStyle from './DocumentMSStyle';
import { updateDocument, getDocumentDetail, uploadFiles } from './dmsApi';

const EditDocumentDialog = (props) => {
  const { classes, open, onClose, document, onSuccess } = props;
  const [formData, setFormData] = useState({
    document_name: '',
    document_type: '',
    client_name: '',
    case_number: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [uploadedNewFiles, setUploadedNewFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && document) {
      console.log('EditDocumentDialog: document received', document);
      const documentId = document.id || document.record_id;
      if (documentId && (!document.document_name || !document.document_type)) {
        // Fetch full document details if only ID is provided or data is incomplete
        fetchDocumentDetails(documentId);
      } else {
        // Use provided document data
        setFormData({
          document_name: document.document_name || '',
          document_type: document.document_type || '',
          client_name: document.client_name || '',
          case_number: document.case_number || '',
          description: document.description || '',
        });
        // Set existing files from document
        if (document.files && Array.isArray(document.files)) {
          setExistingFiles(document.files);
        } else if (document.files) {
          setExistingFiles([document.files]);
        } else {
          setExistingFiles([]);
        }
      }
    } else if (open && !document) {
      console.warn('EditDocumentDialog: open but no document provided');
    }
  }, [open, document]);

  const fetchDocumentDetails = async (documentId) => {
    try {
      setFetching(true);
      console.log('Fetching document details for ID:', documentId);
      const detail = await getDocumentDetail(documentId);
      console.log('Document details fetched:', detail);
      setFormData({
        document_name: detail.document_name || '',
        document_type: detail.document_type || '',
        client_name: detail.client_name || '',
        case_number: detail.case_number || '',
        description: detail.description || '',
      });
      // Set existing files
      if (detail.files && Array.isArray(detail.files)) {
        setExistingFiles(detail.files);
      } else if (detail.files) {
        setExistingFiles([detail.files]);
      } else {
        setExistingFiles([]);
      }
    } catch (err) {
      setError('Failed to load document details');
      console.error('Fetch document error:', err);
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const response = await uploadFiles([file]);
      if (response.documents && response.documents.length > 0) {
        const uploaded = response.documents[0];
        setUploadedNewFiles([...uploadedNewFiles, uploaded]);
        setNewFiles([...newFiles, file]);
      }
    } catch (err) {
      setError(err.error || 'Failed to upload file');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveExistingFile = (fileIndex) => {
    const updated = existingFiles.filter((_, index) => index !== fileIndex);
    setExistingFiles(updated);
  };

  const handleRemoveNewFile = (fileIndex) => {
    const updatedFiles = newFiles.filter((_, index) => index !== fileIndex);
    const updatedUploaded = uploadedNewFiles.filter((_, index) => index !== fileIndex);
    setNewFiles(updatedFiles);
    setUploadedNewFiles(updatedUploaded);
  };

  const handleSubmit = async () => {
    // Validate all required fields
    if (!formData.case_number) {
      setError('Case Number is required');
      return;
    }

    if (!/^\d+$/.test(formData.case_number)) {
      setError('Case Number must contain only numbers');
      return;
    }

    if (!formData.client_name) {
      setError('Client Name is required');
      return;
    }

    if (!formData.document_name) {
      setError('Document name is required');
      return;
    }

    if (!formData.document_type) {
      setError('Document Type is required');
      return;
    }

    if (!formData.description) {
      setError('Description is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const documentId = document?.id || document?.record_id;
      
      // Combine existing files (that weren't removed) with newly uploaded files
      const allFiles = [
        ...existingFiles.map(file => ({
          file_url: file.file_url || file.url,
          summary: file.summary || '',
        })),
        ...uploadedNewFiles.map(file => ({
          file_url: file.file_url || file.url,
          summary: file.summary || '',
        })),
      ];

      const updateData = {
        ...formData,
        documents: allFiles,
      };

      await updateDocument(documentId, updateData);
      
      setSuccess('Document updated successfully!');
      
      // Call success callback after a short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }, 1000);
    } catch (err) {
      setError(err.detail || err.error || 'Failed to update document');
      console.error('Update document error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      document_name: '',
      document_type: '',
      client_name: '',
      case_number: '',
      description: '',
    });
    setExistingFiles([]);
    setNewFiles([]);
    setUploadedNewFiles([]);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const documentName = document?.document_name || 'Document';
  const documentId = document?.id || document?.record_id;
  
  console.log('EditDocumentDialog render - open:', open, 'document:', document, 'documentId:', documentId);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box>
          <Box style={{ fontSize: '24px', fontWeight: 700, color: '#131C4C', fontFamily: 'Montserrat' }}>
            Edit Document
          </Box>
          <Box style={{ fontSize: '14px', color: '#6E759A', marginTop: '4px', fontFamily: 'Montserrat' }}>
            {documentName}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#6E759A',
            '&:hover': {
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        {!document ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Typography>No document selected</Typography>
          </Box>
        ) : fetching ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Box
                className={classes.error}
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#FEE2E2',
                  color: '#DC2626',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                {error}
              </Box>
            )}

            {success && (
              <Box
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#D1FAE5',
                  color: '#059669',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                {success}
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box className={classes.formGroup}>
                  <Box className={classes.label}>Case Number *</Box>
                  <TextField
                    className={classes.input}
                    fullWidth
                    required
                    value={formData.case_number}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers
                      if (value === '' || /^\d+$/.test(value)) {
                        setFormData({ ...formData, case_number: value });
                      }
                    }}
                    placeholder="Enter numbers only (e.g., 2025001)"
                    error={formData.case_number && !/^\d+$/.test(formData.case_number)}
                    helperText={formData.case_number && !/^\d+$/.test(formData.case_number) ? 'Only numbers are allowed' : ''}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.formGroup}>
                  <Box className={classes.label}>Client Name *</Box>
                  <TextField
                    className={classes.input}
                    fullWidth
                    required
                    value={formData.client_name}
                    onChange={handleChange('client_name')}
                    placeholder="e.g., John Doe"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.formGroup}>
                  <Box className={classes.label}>Document Name *</Box>
                  <TextField
                    className={classes.input}
                    fullWidth
                    value={formData.document_name}
                    onChange={handleChange('document_name')}
                    placeholder="e.g., Visa Application Form"
                    required
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box className={classes.formGroup}>
                  <Box className={classes.label}>Document Type *</Box>
                  <TextField
                    className={classes.input}
                    fullWidth
                    required
                    value={formData.document_type}
                    onChange={handleChange('document_type')}
                    placeholder="e.g., visa, passport, permit"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box className={classes.formGroup}>
                  <Box className={classes.label}>Description *</Box>
                  <TextField
                    className={classes.input}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleChange('description')}
                    placeholder="Enter document description"
                    style={{ height: 'auto' }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Existing Files Section */}
            {existingFiles.length > 0 && (
              <Box className={classes.formGroup} style={{ marginTop: '24px' }}>
                <Box className={classes.label} style={{ marginBottom: '12px' }}>Existing Documents</Box>
                <Grid container spacing={2}>
                  {existingFiles.map((file, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <DescriptionIcon style={{ color: '#6E759A', marginRight: '8px' }} />
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              style={{
                                fontSize: '14px',
                                color: '#131C4C',
                                fontFamily: 'Montserrat',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {file.file_name || file.name || `Document ${index + 1}`}
                            </Typography>
                            {file.file_url && (
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon style={{ fontSize: '14px' }} />}
                                onClick={() => window.open(file.file_url || file.url, '_blank')}
                                sx={{
                                  textTransform: 'none',
                                  color: '#0F1638',
                                  fontSize: '12px',
                                  fontFamily: 'Montserrat',
                                  padding: '2px 8px',
                                  marginTop: '4px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(15, 22, 56, 0.08)',
                                  },
                                }}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveExistingFile(index)}
                          sx={{
                            color: '#DC3545',
                            '&:hover': {
                              backgroundColor: 'rgba(220, 53, 69, 0.08)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Upload New Files Section */}
            <Box className={classes.formGroup} style={{ marginTop: '24px' }}>
              <Box className={classes.label} style={{ marginBottom: '12px' }}>
                {existingFiles.length > 0 ? 'Add More Documents' : 'Upload Documents'}
              </Box>
              <Box
                className={classes.fileUploadArea}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed #BCC0D5',
                  borderRadius: '9px',
                  padding: '40px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#F9F9F9',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <CloudUploadIcon
                  style={{
                    fontSize: '48px',
                    color: '#BCC0D5',
                    marginBottom: '16px',
                  }}
                />
                <Box
                  style={{
                    fontSize: '16px',
                    color: '#131C4C',
                    fontWeight: 600,
                    fontFamily: 'Montserrat',
                  }}
                >
                  Click to upload files or drag and drop
                </Box>
                <Box
                  style={{
                    fontSize: '14px',
                    color: '#6E759A',
                    marginTop: '8px',
                    fontFamily: 'Montserrat',
                  }}
                >
                  PDF, Images, and other document formats
                </Box>
                {uploading && (
                  <Box style={{ marginTop: '16px' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>

              {/* Newly Uploaded Files Preview */}
              {uploadedNewFiles.length > 0 && (
                <Box style={{ marginTop: '16px' }}>
                  <Grid container spacing={2}>
                    {uploadedNewFiles.map((file, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            border: '1px solid #4CAF50',
                            borderRadius: '8px',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#F1F8F4',
                          }}
                        >
                          <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <DescriptionIcon style={{ color: '#4CAF50', marginRight: '8px' }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                style={{
                                  fontSize: '14px',
                                  color: '#131C4C',
                                  fontFamily: 'Montserrat',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {newFiles[index]?.name || file.file_name || `New Document ${index + 1}`}
                              </Typography>
                              <Typography
                                style={{
                                  fontSize: '12px',
                                  color: '#4CAF50',
                                  fontFamily: 'Montserrat',
                                  marginTop: '2px',
                                }}
                              >
                                Ready to save
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveNewFile(index)}
                            sx={{
                              color: '#DC3545',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.08)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px 24px',
          borderTop: '1px solid #E5E7EB',
          gap: '12px',
        }}
      >
        <Button
          onClick={handleClose}
          className={classes.buttonSecondary}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          className={classes.button}
          disabled={loading || fetching}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(DocumentMSStyle)(EditDocumentDialog);

